import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { FILTER_INPUT, FilterField, FilterPanel } from "../components/common/FilterBar.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { erroDeCampo } from "../lib/erro.js";
import { useT } from "../i18n/index.js";
import { sugerirTipoCliente } from "@eurostorm/shared";
import {
  CustomerReviewModal,
  type ClienteParaRevisar,
} from "../components/domain/CustomerReviewModal.js";
import { Paginacao } from "../components/common/Paginacao.js";

type Kind = "individual" | "business" | "insurer";
type Lang = "fr" | "en" | "pt";

const PAGE_SIZE = 50;

interface CustomerForm {
  kind: Kind;
  displayName: string;
  legalName: string;
  fiscalId: string;
  fiscalIdKind: string;
  countryCode: string;
  vatNumber: string;
  email: string;
  billingEmail: string;
  phone: string;
  preferredLang: Lang;
  paymentTermDays: number;
  /**
   * F-customer-pct — % comissão/retenção do cliente (string p/ permitir vazio).
   * Usado em base orçamento: auto-preenche customerRetentionPct no dossier.
   */
  defaultRetentionPct: string;
  franchiseRequired: boolean;
  /** F-invoice-fiscal — fatura aplica TVA? (false = autoliquidação intra-UE). */
  appliesTva: boolean;
  tvaRate: string;
  /** F-customer-tva-regime — quando !appliesTva: true=autoliquidação, false=isento. */
  tvaReverseCharge: boolean;
  notes: string;
  /** F-chantier-entity — chantier do cliente ("" = nenhum). */
  chantierId: string;
  chantierManagerPct: string;
  /** F-chantier-agenda — conta do portal do cliente ("" = nenhuma). */
  portalUserId: string;
  reason: string;
}

const emptyForm: CustomerForm = {
  kind: "individual",
  displayName: "",
  legalName: "",
  fiscalId: "",
  fiscalIdKind: "",
  countryCode: "FR",
  vatNumber: "",
  email: "",
  billingEmail: "",
  phone: "",
  preferredLang: "fr",
  paymentTermDays: 30,
  defaultRetentionPct: "",
  franchiseRequired: false,
  appliesTva: true,
  tvaRate: "20",
  tvaReverseCharge: true,
  notes: "",
  chantierId: "",
  chantierManagerPct: "",
  portalUserId: "",
  reason: "",
};

export default function Customers() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const purgeUtils = trpc.useUtils();
  const companiesQuery = trpc.companies.list.useQuery();
  // F-customer-purge — desfazer import acidental: exclui cliente + dossiers
  // VIRGENS + veículos (super admin; bloqueado se houver qualquer movimento).
  const purgeMut = trpc.customers.purge.useMutation({
    onSuccess: (r) => {
      void purgeUtils.customers.invalidate();
      void modals.alert({
        title: t("customers.purgeDoneTitle"),
        message: `${r.name}: ${r.dossiersDeleted} ${t("customers.purgeDoneDossiers")} · ${r.vehiclesDeleted} ${t("customers.purgeDoneVehicles")}`,
      });
    },
    onError: (e) =>
      void modals.alert({ title: t("customers.purgeTitle"), message: e.message, tone: "danger" }),
  });
  async function handlePurge(c: { id: number; displayName: string }) {
    const ok = await modals.confirm({
      title: `${t("customers.purgeTitle")} — ${c.displayName}`,
      message: t("customers.purgeConfirmMsg"),
      tone: "danger",
      confirmLabel: t("customers.purgeBtn"),
    });
    if (!ok || companyId === null) return;
    const reason = await modals.promptReason({
      title: t("customers.purgeReasonTitle"),
      placeholder: t("customers.purgeReasonPlaceholder"),
      required: true,
    });
    if (!reason) return;
    purgeMut.mutate({ companyId, customerId: c.id, reason });
  }

  const accessibleCompanyIds = user?.allowedCompanyIds ?? [];
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    accessibleCompanyIds.includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState<"" | Kind>("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("active");
  const [offset, setOffset] = useState(0);
  // CEO 2026-10-27 (opção C) — fila "a revisar" (o Painel abre com ?revisar=1).
  const [reviewFilter, setReviewFilter] = useState(
    () => new URLSearchParams(window.location.search).get("revisar") === "1",
  );
  const [reviewing, setReviewing] = useState<ClienteParaRevisar | null>(null);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      q: q || undefined,
      kind: kindFilter || undefined,
      active: activeFilter === "all" ? undefined : activeFilter === "active",
      reviewPending: reviewFilter ? true : undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, q, kindFilter, activeFilter, reviewFilter, offset],
  );

  const listQuery = trpc.customers.list.useQuery(filters, {
    enabled: companyId !== null,
  });

  const utils = trpc.useUtils();
  const refetch = () => {
    // Invalida TODAS as queries do router customers: list, getById, search.
    // Sem isso, edições no banco (via API ou outra aba) deixam o cache do modal
    // com valores stale e o backend rejeita updates como "alteração fiscal sem motivo".
    utils.customers.invalidate();
    listQuery.refetch();
  };

  const [modal, setModal] = useState<
    { mode: "create" } | { mode: "edit"; customerId: number } | null
  >(null);

  const t = useT();

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("customers.noAccess")}</p>
      </Shell>
    );
  }

  const data = listQuery.data;
  const total = data?.total ?? 0;

  return (
    <Shell>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <FilterField label={t("customers.companyLabel")}>
          <select
            value={companyId ?? ""}
            onChange={(e) => {
              setCompanyId(Number(e.target.value));
              setOffset(0);
            }}
            className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none w-64"
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
        {canEdit && (
          <button
            onClick={() => setModal({ mode: "create" })}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
          >
            + {t("customers.newBtn")}
          </button>
        )}
      </div>

      {/* F-customers-kind-counters — botões-contadores estilo eDSP: clicar
          filtra por Pro / Particulier / Total. Contagens ignoram o filtro de
          tipo (mostram sempre o universo atual de busca/status). */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => {
            setKindFilter(kindFilter === "business" ? "" : "business");
            setOffset(0);
          }}
          className="rounded-lg px-4 py-3 text-center font-semibold text-white"
          style={{
            background: "#2563EB",
            outline: kindFilter === "business" ? "3px solid #93C5FD" : "none",
          }}
        >
          <span className="block text-xl">{data?.counts?.business ?? "…"}</span>
          <span className="text-xs opacity-90">{t("customers.kindBusiness")}</span>
        </button>
        <button
          onClick={() => {
            setKindFilter(kindFilter === "individual" ? "" : "individual");
            setOffset(0);
          }}
          className="rounded-lg px-4 py-3 text-center font-semibold text-white"
          style={{
            background: "#DC2626",
            outline: kindFilter === "individual" ? "3px solid #FCA5A5" : "none",
          }}
        >
          <span className="block text-xl">{data?.counts?.individual ?? "…"}</span>
          <span className="text-xs opacity-90">{t("customers.kindIndividual")}</span>
        </button>
        <button
          onClick={() => {
            setKindFilter("");
            setOffset(0);
          }}
          className="rounded-lg px-4 py-3 text-center font-semibold text-white"
          style={{
            background: "#1F2937",
            outline: kindFilter === "" ? "3px solid #6B7280" : "none",
          }}
        >
          <span className="block text-xl">{data?.counts?.total ?? "…"}</span>
          <span className="text-xs opacity-90">{t("customers.totalLabel")}</span>
        </button>
      </div>
      {((data?.counts?.reviewPending ?? 0) > 0 || reviewFilter) && (
        <div>
          <button
            type="button"
            onClick={() => {
              setReviewFilter((v) => !v);
              setOffset(0);
            }}
            aria-pressed={reviewFilter}
            data-testid="customers-review-chip"
            className="inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold"
            style={{
              background: reviewFilter ? "#D97706" : "#FEF3C7",
              borderColor: "#D97706",
              color: reviewFilter ? "#FFFFFF" : "#78350F",
            }}
          >
            🔎 {t("customerReview.chip")}
            <span className="rounded-full bg-black/10 px-1.5 text-[10px] tabular-nums">
              {data?.counts?.reviewPending ?? "…"}
            </span>
          </button>
        </div>
      )}

      <FilterPanel testId="customers-filter-panel">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <FilterField label={t("customers.searchLabel")} className="grow max-w-xs">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder={t("customers.searchPlaceholder")}
              className={FILTER_INPUT + " w-full"}
            />
          </FilterField>
          <FilterField label={t("customers.typeLabel")}>
            <select
              value={kindFilter}
              onChange={(e) => {
                setKindFilter(e.target.value as Kind | "");
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full sm:w-44"}
            >
              <option value="">{t("customers.typeAll")}</option>
              <option value="individual">{t("customers.kindIndividual")}</option>
              <option value="business">{t("customers.kindBusiness")}</option>
              <option value="insurer">{t("customers.kindInsurer")}</option>
            </select>
          </FilterField>
          <FilterField label={t("customers.statusLabel")}>
            <select
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as typeof activeFilter);
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full sm:w-40"}
            >
              <option value="active">{t("customers.statusActive")}</option>
              <option value="inactive">{t("customers.statusInactive")}</option>
              <option value="all">{t("customers.statusAll")}</option>
            </select>
          </FilterField>
        </div>
      </FilterPanel>

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("customers.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("customers.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && isMobile && (
          <div data-testid="customers-cards" className="divide-y divide-neutral-800">
            {data.rows.map((c) => (
              <div
                key={c.id}
                className={`px-3 py-3 ${c.active ? "" : "opacity-60"}`}
                data-testid="customer-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      href={`/clientes/${c.id}`}
                      className="break-words text-base font-medium hover:text-lime hover:underline"
                      data-testid="customer-name-link"
                    >
                      {c.displayName}
                    </Link>
                    {c.legalName && <div className="text-xs text-neutral-500">{c.legalName}</div>}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <KindBadge kind={c.kind as Kind} />
                    {c.reviewPending && <ReviewBadge />}
                    {!c.active && <ActiveBadge active={c.active} />}
                  </div>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-neutral-400">
                  {c.chantierName && (
                    <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-300">
                      🏗 {c.chantierName}
                    </span>
                  )}
                  {c.fiscalId && <span className="tabular-nums">{c.fiscalId}</span>}
                  <span>{c.countryCode}</span>
                  {c.email && <span className="break-all">{c.email}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Link
                    href={`/clientes/${c.id}`}
                    className="inline-flex h-10 items-center rounded-md border border-lime/40 px-3 text-xs font-medium text-lime"
                  >
                    {t("customers.openLink")}
                  </Link>
                  {canEdit && c.reviewPending && (
                    <button
                      onClick={() => setReviewing(c)}
                      className="inline-flex h-10 items-center rounded-md border px-3 text-xs font-semibold"
                      style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
                    >
                      🔎 {t("customerReview.reviewBtn")}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => setModal({ mode: "edit", customerId: c.id })}
                      className="inline-flex h-10 items-center rounded-md border border-neutral-700 px-3 text-xs text-neutral-300"
                    >
                      {t("customers.editLink")}
                    </button>
                  )}
                  {user?.isSuperAdmin && (
                    <button
                      onClick={() => void handlePurge(c)}
                      disabled={purgeMut.isPending}
                      className="inline-flex h-10 items-center rounded-md border border-red-500/40 px-3 text-xs text-red-400"
                      title={t("customers.purgeBtnTitle")}
                    >
                      {t("customers.purgeBtn")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {data && data.rows.length > 0 && !isMobile && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">{t("customers.colName")}</th>
                <th className="text-left px-3 py-2 w-24">{t("customers.colType")}</th>
                <th className="text-left px-3 py-2 w-44">{t("customers.colFiscalId")}</th>
                <th className="text-left px-3 py-2 w-16">{t("customers.colCountry")}</th>
                <th className="text-left px-3 py-2">{t("customers.colEmail")}</th>
                <th className="text-left px-3 py-2 w-20">{t("customers.colStatus")}</th>
                <th className="px-3 py-2 w-20 text-right">{t("customers.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.rows.map((c) => (
                <tr key={c.id} className={c.active ? "" : "opacity-60"}>
                  <td className="px-3 py-2">
                    {/* CEO 2026-10-06: o nome do cliente abre a ficha dele. */}
                    <Link
                      href={`/clientes/${c.id}`}
                      className="font-medium hover:text-lime hover:underline"
                      data-testid="customer-name-link"
                    >
                      {c.displayName}
                    </Link>
                    {c.legalName && <div className="text-xs text-neutral-500">{c.legalName}</div>}
                    {c.chantierName && (
                      <span className="mt-0.5 inline-block rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-300">
                        🏗 {c.chantierName}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <KindBadge kind={c.kind as Kind} />
                    {c.reviewPending && (
                      <div className="mt-1">
                        <ReviewBadge />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">
                    {c.fiscalId ?? <span className="text-neutral-700">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{c.countryCode}</td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{c.email ?? "—"}</td>
                  <td className="px-3 py-2">
                    <ActiveBadge active={c.active} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Link
                        href={`/clientes/${c.id}`}
                        className="text-xs text-lime hover:underline"
                      >
                        {t("customers.openLink")}
                      </Link>
                      {canEdit && c.reviewPending && (
                        <button
                          onClick={() => setReviewing(c)}
                          className="whitespace-nowrap text-xs font-semibold hover:underline"
                          style={{ color: "#B45309" }}
                          data-testid="customer-review-btn"
                        >
                          🔎 {t("customerReview.reviewBtn")}
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => setModal({ mode: "edit", customerId: c.id })}
                          className="text-xs text-neutral-400 hover:underline"
                        >
                          {t("customers.editLink")}
                        </button>
                      )}
                      {user?.isSuperAdmin && (
                        <button
                          onClick={() => void handlePurge(c)}
                          disabled={purgeMut.isPending}
                          className="text-xs text-red-400 hover:underline"
                          title={t("customers.purgeBtnTitle")}
                        >
                          {t("customers.purgeBtn")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={total === 1 ? t("customers.customerSingular") : t("customers.customerPlural")}
          testId="customers-pagination"
        />
      )}

      {canEdit && reviewing && companyId !== null && (
        <CustomerReviewModal
          companyId={companyId}
          customer={reviewing}
          onClose={() => setReviewing(null)}
        />
      )}
      {canEdit && modal && companyId !== null && (
        <CustomerModal
          companyId={companyId}
          mode={modal.mode}
          customerId={modal.mode === "edit" ? modal.customerId : undefined}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refetch();
          }}
        />
      )}
    </Shell>
  );
}

export function CustomerModal({
  companyId,
  mode,
  customerId,
  onClose,
  onSaved,
}: {
  companyId: number;
  mode: "create" | "edit";
  customerId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const modals = useModals();
  const t = useT();
  const detailQuery = trpc.customers.getById.useQuery(
    { companyId, customerId: customerId ?? 0 },
    {
      enabled: mode === "edit" && !!customerId,
      retry: false,
      // Sempre buscar fresco ao abrir o modal — o estado pode ter mudado por
      // outra aba/API entre a listagem e o edit. Cache stale aqui causa o
      // backend rejeitar updates como "alteração fiscal sem reason".
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const [form, setForm] = useState<CustomerForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  // F-erros-i18n — campo apontado pelo servidor (ex.: nº fiscal duplicado).
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  // o modal é longo: leva o usuário até o campo com erro (depois do render).
  useEffect(() => {
    if (!fieldError) return;
    const el = document.querySelector<HTMLElement>("[aria-invalid=true]");
    el?.scrollIntoView({ block: "center" });
    el?.focus();
  }, [fieldError]);

  // Popula o form quando o detail chega (ou quando trocar o customer editado).
  // Não usa "initialized" porque queremos repopular se o usuário fechar e
  // reabrir o modal no mesmo customer (refetch fresh do servidor).
  useEffect(() => {
    if (mode !== "edit") return;
    const d = detailQuery.data;
    if (!d) return;
    setForm({
      kind: d.kind as Kind,
      displayName: d.displayName,
      legalName: d.legalName ?? "",
      fiscalId: d.fiscalId ?? "",
      fiscalIdKind: d.fiscalIdKind ?? "",
      countryCode: d.countryCode,
      vatNumber: d.vatNumber ?? "",
      email: d.email ?? "",
      billingEmail: d.billingEmail ?? "",
      phone: d.phone ?? "",
      preferredLang: d.preferredLang as Lang,
      paymentTermDays: d.paymentTermDays ?? 30,
      defaultRetentionPct: d.defaultRetentionPct != null ? String(d.defaultRetentionPct) : "",
      franchiseRequired: d.franchiseRequired ?? false,
      appliesTva: d.appliesTva ?? true,
      tvaRate: d.tvaRate != null ? String(d.tvaRate) : "20",
      tvaReverseCharge: d.tvaReverseCharge ?? true,
      notes: d.notes ?? "",
      chantierId: d.chantierId != null ? String(d.chantierId) : "",
      chantierManagerPct: d.chantierManagerPct != null ? String(Number(d.chantierManagerPct)) : "",
      portalUserId: d.userId != null ? String(d.userId) : "",
      reason: "",
    });
  }, [mode, detailQuery.data?.id, detailQuery.dataUpdatedAt]);

  const fiscalChanged =
    mode === "edit" && detailQuery.data
      ? form.fiscalId !== (detailQuery.data.fiscalId ?? "") ||
        form.fiscalIdKind !== (detailQuery.data.fiscalIdKind ?? "")
      : false;

  const isAdmin =
    user?.isSuperAdmin ||
    user?.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin") ||
    false;

  // F-chantier-entity — chantiers ativos para o select (como no eDSP).
  const chantiersQuery = trpc.chantiers.list.useQuery(
    { companyId, activeOnly: true },
    { enabled: companyId > 0 },
  );
  // F-chantier-agenda — contas da empresa (admin) para vincular o portal.
  const companyUsersQuery = trpc.users.listByCompany.useQuery(
    { companyId },
    { enabled: companyId > 0 && isAdmin },
  );
  const onServerError = (err: unknown) => {
    const { field, message } = erroDeCampo(err);
    if (field) setFieldError({ field, message });
    else setError(message);
  };
  const createMutation = trpc.customers.create.useMutation({
    onSuccess: onSaved,
    onError: onServerError,
  });
  const updateMutation = trpc.customers.update.useMutation({
    onSuccess: onSaved,
    onError: onServerError,
  });
  const deactivateMutation = trpc.customers.deactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const reactivateMutation = trpc.customers.reactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deactivateMutation.isPending ||
    reactivateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldError(null);

    // F-customer-pct — vazio = null no banco; valor = parse + valida 0..100.
    let defaultRetentionPctNum: number | null | undefined = undefined;
    if (form.defaultRetentionPct.trim() === "") {
      defaultRetentionPctNum = mode === "edit" ? null : undefined;
    } else {
      const n = Number(form.defaultRetentionPct);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        setError(t("customers.commissionRangeError"));
        return;
      }
      defaultRetentionPctNum = n;
    }

    const payload = {
      companyId,
      kind: form.kind,
      displayName: form.displayName.trim(),
      legalName: form.legalName.trim() || undefined,
      fiscalId: form.fiscalId.trim() || undefined,
      fiscalIdKind: form.fiscalIdKind.trim() || undefined,
      countryCode: form.countryCode.trim().toUpperCase(),
      vatNumber: form.vatNumber.trim() || undefined,
      // CEO 2026-11-01 — campo apagado na edição apaga no cadastro.
      email: form.email.trim() || (mode === "edit" ? null : undefined),
      billingEmail: form.billingEmail.trim() || (mode === "edit" ? null : undefined),
      phone: form.phone.trim() || (mode === "edit" ? null : undefined),
      preferredLang: form.preferredLang,
      paymentTermDays: form.paymentTermDays,
      defaultRetentionPct: defaultRetentionPctNum,
      franchiseRequired: form.franchiseRequired,
      appliesTva: form.appliesTva,
      tvaRate: form.tvaRate.trim() === "" ? undefined : Number(form.tvaRate),
      tvaReverseCharge: form.tvaReverseCharge,
      notes: form.notes.trim() || undefined,
      chantierId:
        form.chantierId === "" ? (mode === "edit" ? null : undefined) : Number(form.chantierId),
      chantierManagerPct:
        form.chantierManagerPct.trim() === ""
          ? mode === "edit"
            ? null
            : undefined
          : Number(form.chantierManagerPct.replace(",", ".")),
      userId:
        form.portalUserId === "" ? (mode === "edit" ? null : undefined) : Number(form.portalUserId),
    };

    if (mode === "create") {
      createMutation.mutate(payload);
    } else if (customerId) {
      const { companyId: _drop, ...patch } = payload;
      updateMutation.mutate({
        companyId,
        customerId,
        patch,
        reason: form.reason.trim() || undefined,
      });
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <h2 className="text-lg font-semibold">
            {mode === "create" ? t("customers.newBtn") : t("customers.editTitle")}
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
            aria-label={t("customers.close")}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("customers.fieldDisplayName")} required>
              <input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                required
                maxLength={200}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("customers.fieldLegalName")}>
              <input
                value={form.legalName}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
                maxLength={255}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("customers.typeLabel")}>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as Kind })}
                disabled={pending}
                className={inputClass}
              >
                <option value="individual">{t("customers.kindIndividual")}</option>
                <option value="business">{t("customers.kindBusiness")}</option>
                <option value="insurer">{t("customers.kindInsurer")}</option>
              </select>
              {/* CEO 2026-10-27 (MELANIE FAURE) — PRO sem SIRET, TVA nem remise
                  costuma ser particular cadastrado errado: a comissão em base
                  orçamento não sai sem a remise. */}
              {form.kind === "business" &&
                !form.fiscalId.trim() &&
                !form.vatNumber.trim() &&
                form.defaultRetentionPct.trim() === "" && (
                  <p
                    className="mt-1 rounded-md border px-2 py-1 text-xs leading-snug"
                    style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
                    data-testid="customer-pro-looks-individual"
                  >
                    ⚠{" "}
                    {sugerirTipoCliente({ nome: form.displayName, razaoSocial: form.legalName })
                      .kind === "individual"
                      ? t("customers.proLooksIndividual")
                      : t("customerReview.proMissingRemise")}
                  </p>
                )}
            </Field>
            <Field label={t("customers.fieldCountry")} required>
              <input
                value={form.countryCode}
                onChange={(e) =>
                  setForm({ ...form, countryCode: e.target.value.toUpperCase().slice(0, 2) })
                }
                required
                maxLength={2}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("customers.colFiscalId")}>
              <input
                value={form.fiscalId}
                onChange={(e) => {
                  setForm({ ...form, fiscalId: e.target.value });
                  if (fieldError?.field === "fiscalId") setFieldError(null);
                }}
                maxLength={50}
                disabled={pending || (mode === "edit" && !isAdmin)}
                aria-invalid={fieldError?.field === "fiscalId" || undefined}
                className={`${inputClass} ${fieldError?.field === "fiscalId" ? "border-red-500!" : ""}`}
              />
              {fieldError?.field === "fiscalId" && (
                <p
                  className="mt-1 text-xs text-red-400"
                  role="alert"
                  data-testid="field-error-fiscalId"
                >
                  {fieldError.message}
                </p>
              )}
              {mode === "edit" && !isAdmin && (
                <p className="text-xs text-neutral-600 mt-1">{t("customers.fiscalAdminOnly")}</p>
              )}
            </Field>
            <Field label={t("customers.fieldFiscalIdKind")}>
              <select
                value={form.fiscalIdKind}
                onChange={(e) => setForm({ ...form, fiscalIdKind: e.target.value })}
                disabled={pending || (mode === "edit" && !isAdmin)}
                className={inputClass}
              >
                <option value="">—</option>
                <option value="siret">SIRET (FR)</option>
                <option value="vat_fr">TVA (FR)</option>
                <option value="vat_gb">VAT (UK)</option>
                <option value="vat_de">USt-IdNr. (DE)</option>
                <option value="vat_it">Partita IVA (IT)</option>
                <option value="vat_es">NIF/CIF (ES)</option>
                <option value="vat_eu">{t("customers.fiscalKindVatEu")}</option>
                <option value="cnpj">CNPJ (BR)</option>
                <option value="ee_reg">Äriregister (EE)</option>
                <option value="other">{t("customers.fiscalKindOther")}</option>
              </select>
            </Field>
            <Field label={t("customers.fieldVatNumber")}>
              <input
                value={form.vatNumber}
                onChange={(e) => setForm({ ...form, vatNumber: e.target.value.toUpperCase() })}
                maxLength={50}
                disabled={pending}
                placeholder="FR12345678901 · DE123456789 · IT12345678901 · ESX1234567X · GB123456789"
                className={`${inputClass} font-mono`}
              />
            </Field>
            <Field label={t("customers.fieldPhone")}>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                maxLength={50}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("customers.fieldEmail")}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("customers.fieldBillingEmail")}>
              <input
                type="email"
                value={form.billingEmail}
                onChange={(e) => setForm({ ...form, billingEmail: e.target.value })}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("customers.fieldPreferredLang")}>
              <select
                value={form.preferredLang}
                onChange={(e) => setForm({ ...form, preferredLang: e.target.value as Lang })}
                disabled={pending}
                className={inputClass}
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </Field>
            <Field label={t("customers.fieldPaymentTerm")}>
              <input
                type="number"
                min={0}
                max={365}
                value={form.paymentTermDays}
                onChange={(e) => setForm({ ...form, paymentTermDays: Number(e.target.value) || 0 })}
                disabled={pending}
                className={inputClass}
              />
            </Field>
          </div>

          {/* F-customer-pct — % Comissão default do cliente (retenção sobre orçamento).
              Auto-preenche customerRetentionPct ao criar dossier em base ORÇAMENTO. */}
          <Field label={t("customers.fieldCommission")}>
            <input
              type="number"
              step="0.01"
              min={0}
              max={100}
              value={form.defaultRetentionPct}
              onChange={(e) => setForm({ ...form, defaultRetentionPct: e.target.value })}
              disabled={pending}
              placeholder={t("customers.placeholderEx30")}
              className={inputClass}
            />
            <p className="text-[11px] text-text-muted mt-1">{t("customers.commissionHelp")}</p>
          </Field>

          {/* F1 franquia (brief CEO 2026-09-18) — exigência por cliente B2B
              (particular SEMPRE exige, o flag é ignorado). */}
          <Field label={t("customers.franchiseRequiredLabel")}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.franchiseRequired}
                onChange={(e) => setForm({ ...form, franchiseRequired: e.target.checked })}
                disabled={pending}
              />
              {t("customers.franchiseRequiredToggle")}
            </label>
            <p className="text-[11px] text-text-muted mt-1">
              {t("customers.franchiseRequiredHelp")}
            </p>
          </Field>

          {/* F-atelier (CEO 2026-09-19) — ateliês/locais de serviço do
              cliente (ex.: CHIR-AUTO P5 → Julien, Garage Peugeot St
              Alexandre). O técnico escolhe o ateliê ao importar o devis. */}
          {mode === "edit" && customerId != null && (
            <AteliersSection companyId={companyId} customerId={customerId} />
          )}

          {/* F-carro-parc (CEO 2026-09-26) — o cliente aceita "carro de parc":
              categorias com valor fixo (cliente / débosselage / montagem) que o
              técnico escolhe no portal quando o carro não tem devis. */}
          {mode === "edit" && customerId != null && (
            <ParcCarsSection companyId={companyId} customerId={customerId} />
          )}

          {/* F-invoice-fiscal — TVA da fatura para este cliente. */}
          <Field label={t("customers.fieldTvaOnInvoice")}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.appliesTva}
                onChange={(e) => setForm({ ...form, appliesTva: e.target.checked })}
                disabled={pending}
                className="accent-lime"
              />
              {t("customers.appliesTvaLabel")}
            </label>
            {form.appliesTva ? (
              <input
                type="number"
                step="0.01"
                min={0}
                max={30}
                value={form.tvaRate}
                onChange={(e) => setForm({ ...form, tvaRate: e.target.value })}
                disabled={pending}
                placeholder={t("customers.placeholderEx20")}
                className={inputClass + " mt-2"}
              />
            ) : (
              // F-customer-tva-regime — sem TVA: escolher o MOTIVO (muda a menção no PDF).
              <select
                value={form.tvaReverseCharge ? "reverse_charge" : "exempt"}
                onChange={(e) =>
                  setForm({ ...form, tvaReverseCharge: e.target.value === "reverse_charge" })
                }
                disabled={pending}
                className={inputClass + " mt-2"}
              >
                <option value="reverse_charge">{t("customers.tvaReverseChargeOption")}</option>
                <option value="exempt">{t("customers.tvaExemptOption")}</option>
              </select>
            )}
            <p className="text-[11px] text-text-muted mt-1">
              {form.appliesTva
                ? t("customers.tvaHelpApplies")
                : form.tvaReverseCharge
                  ? t("customers.tvaHelpReverse")
                  : t("customers.tvaHelpExempt")}
            </p>
          </Field>

          <Field label={t("customers.fieldChantier")}>
            <select
              value={form.chantierId}
              onChange={(e) => setForm({ ...form, chantierId: e.target.value })}
              disabled={pending}
              className={inputClass}
            >
              <option value="">{t("customers.chantierNone")}</option>
              {(chantiersQuery.data?.rows ?? []).map((ch) => (
                <option key={ch.id} value={ch.id}>
                  {ch.name}
                  {ch.city ? ` — ${ch.city}` : ""}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-neutral-500 mt-1">{t("customers.chantierHint")}</p>
          </Field>

          {/* CEO 2026-10-31 — exceção da comissão do gerente do chantier só
              neste cliente (vazio = padrão do cadastro do gerente). */}
          {isAdmin && form.chantierId !== "" && (
            <Field label={t("customers.fieldManagerPct")}>
              <input
                type="text"
                inputMode="decimal"
                value={form.chantierManagerPct}
                onChange={(e) => setForm({ ...form, chantierManagerPct: e.target.value })}
                disabled={pending}
                placeholder={t("customers.managerPctPlaceholder")}
                className={inputClass + " w-40"}
                data-testid="customer-manager-pct"
              />
              <p className="text-[11px] text-neutral-500 mt-1">{t("customers.managerPctHint")}</p>
            </Field>
          )}

          {isAdmin && (
            <Field label={t("customers.fieldPortalUser")}>
              <select
                value={form.portalUserId}
                onChange={(e) => setForm({ ...form, portalUserId: e.target.value })}
                disabled={pending}
                className={inputClass}
              >
                <option value="">{t("customers.portalUserNone")}</option>
                {(companyUsersQuery.data ?? []).map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-neutral-500 mt-1">{t("customers.portalUserHint")}</p>
            </Field>
          )}

          <Field label={t("customers.fieldNotes")}>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={2000}
              rows={2}
              disabled={pending}
              className={`${inputClass} resize-y`}
            />
          </Field>

          {mode === "edit" && fiscalChanged && (
            <Field label={t("customers.fieldReason")} required>
              <input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                required
                maxLength={500}
                disabled={pending}
                placeholder={t("customers.reasonPlaceholder")}
                className={inputClass}
              />
            </Field>
          )}

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
            <div className="flex gap-2">
              {mode === "edit" && customerId && isAdmin && detailQuery.data && (
                <>
                  {detailQuery.data.active ? (
                    <button
                      type="button"
                      onClick={async () => {
                        const reason = await modals.promptReason({
                          title: `${t("customers.deactivateTitlePre")} "${detailQuery.data?.displayName ?? ""}"`,
                          message: t("customers.deactivateMessage"),
                          placeholder: t("customers.deactivatePlaceholder"),
                          confirmLabel: t("customers.deactivate"),
                          tone: "danger",
                          required: true,
                        });
                        if (!reason) return;
                        deactivateMutation.mutate({ companyId, customerId, reason });
                      }}
                      disabled={pending}
                      className="rounded-md border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-1.5 text-xs"
                    >
                      {t("customers.deactivate")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => reactivateMutation.mutate({ companyId, customerId })}
                      disabled={pending}
                      className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs"
                    >
                      {t("customers.reactivate")}
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={pending}
                className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
              >
                {t("customers.cancel")}
              </button>
              <button
                type="submit"
                disabled={pending}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              >
                {pending
                  ? t("customers.saving")
                  : mode === "create"
                    ? t("customers.create")
                    : t("customers.save")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}

function KindBadge({ kind }: { kind: Kind }) {
  const t = useT();
  const map: Record<Kind, { label: string; class: string }> = {
    individual: { label: t("customers.badgePart"), class: "bg-blue-500/20 text-blue-300" },
    business: { label: t("customers.badgeProf"), class: "bg-amber-500/20 text-amber-300" },
    insurer: { label: t("customers.badgeInsurer"), class: "bg-purple-500/20 text-purple-300" },
  };
  const m = map[kind];
  return <span className={`inline-block rounded px-2 py-0.5 text-xs ${m.class}`}>{m.label}</span>;
}

function ReviewBadge() {
  const t = useT();
  return (
    <span
      className="inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold"
      style={{ background: "#FEF3C7", color: "#78350F" }}
    >
      {t("customerReview.badge")}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  const t = useT();
  return active ? (
    <span className="inline-block rounded bg-lime/20 text-lime px-2 py-0.5 text-xs">
      {t("customers.badgeActive")}
    </span>
  ) : (
    <span className="inline-block rounded bg-neutral-700/40 text-neutral-400 px-2 py-0.5 text-xs">
      {t("customers.badgeInactive")}
    </span>
  );
}

/**
 * F-atelier — gerenciador de ateliês do cliente dentro do modal de edição:
 * lista + adicionar (nome, cidade) + remover.
 */
function ParcCarsSection({ companyId, customerId }: { companyId: number; customerId: number }) {
  const t = useT();
  const [, setLocation] = useLocation();
  const listQ = trpc.hail.parcs.list.useQuery({ companyId });
  const createMut = trpc.hail.parcs.create.useMutation({
    onSuccess: (p) => setLocation(`/parc-grele/${p.id}`),
  });
  const parcs = (listQ.data ?? []).filter((p) => p.customerId === customerId);
  return (
    <div
      className="space-y-1.5 rounded-md border border-neutral-800 p-2"
      data-testid="parc-cars-section"
    >
      <p className="text-xs font-semibold">🅿 {t("customers.parcCarsTitle")}</p>
      <p className="text-[11px] text-text-muted">{t("customers.parcCarsHelp")}</p>
      {parcs.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-2 text-sm">
          <span className="min-w-0 truncate">
            {p.name}{" "}
            <span className="text-[11px] text-text-muted">
              · {p.status === "open" ? t("customers.parcOpen") : t("customers.parcClosed")}
            </span>
          </span>
          <Link href={`/parc-grele/${p.id}`} className="text-xs text-lime hover:underline">
            {t("customers.parcEditCategories")} »
          </Link>
        </div>
      ))}
      {parcs.length === 0 && (
        <button
          type="button"
          disabled={createMut.isPending}
          onClick={() =>
            createMut.mutate({ companyId, customerId, name: t("customers.parcDefaultName") })
          }
          className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs hover:border-lime hover:text-lime disabled:opacity-40"
          data-testid="parc-cars-enable"
        >
          + {t("customers.parcEnableBtn")}
        </button>
      )}
      {createMut.error && <p className="text-xs text-red-400">{createMut.error.message}</p>}
    </div>
  );
}

function AteliersSection({ companyId, customerId }: { companyId: number; customerId: number }) {
  const t = useT();
  const utils = trpc.useUtils();
  const listQ = trpc.customers.listAteliers.useQuery({ companyId, customerId });
  const addMut = trpc.customers.addAtelier.useMutation({
    onSuccess: () => utils.customers.listAteliers.invalidate(),
  });
  const removeMut = trpc.customers.removeAtelier.useMutation({
    onSuccess: () => utils.customers.listAteliers.invalidate(),
  });
  // F-tarifa-atelier (CEO 2026-10-10) — remise do cliente POR ateliê/projeto e
  // os nomes pelos quais o eDSP chama o projeto (o import casa pelo alias).
  const updateMut = trpc.customers.updateAtelier.useMutation({
    onSuccess: () => utils.customers.listAteliers.invalidate(),
  });
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [remise, setRemise] = useState("");
  const [aliases, setAliases] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [editRemise, setEditRemise] = useState("");
  const [editAliases, setEditAliases] = useState("");
  const splitAliases = (v: string) =>
    v
      .split(/[,;\n]/)
      .map((x) => x.trim())
      .filter(Boolean);
  const pctOk = (v: string) =>
    v === "" || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100);
  const inp =
    "min-w-0 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm outline-none focus:border-lime";
  return (
    <div className="space-y-1.5 rounded-md border border-neutral-800 p-2">
      <p className="text-xs font-semibold">🏢 {t("customers.ateliersTitle")}</p>
      <p className="text-[11px] text-text-muted">{t("customers.ateliersHelp")}</p>
      {(listQ.data?.rows ?? []).map((a) => (
        <div key={a.id} className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate">
              {a.name}
              {a.city ? ` — ${a.city}` : ""}
              <span className="ml-2 text-[11px] text-text-muted">
                {a.defaultRetentionPct != null
                  ? `${t("customers.atelierRemiseLabel")} ${Number(a.defaultRetentionPct)} %`
                  : t("customers.atelierRemiseCustomer")}
                {a.aliases && a.aliases.length > 0 && ` · eDSP: ${a.aliases.join(", ")}`}
              </span>
            </span>
            <span className="flex shrink-0 gap-1">
              <button
                type="button"
                onClick={() => {
                  setEditId(editId === a.id ? null : a.id);
                  setEditRemise(
                    a.defaultRetentionPct != null ? String(Number(a.defaultRetentionPct)) : "",
                  );
                  setEditAliases((a.aliases ?? []).join(", "));
                }}
                className="rounded border border-neutral-700 px-2 py-0.5 text-xs text-neutral-300 hover:border-lime"
                data-testid={`atelier-edit-${a.id}`}
              >
                ✎
              </button>
              <button
                type="button"
                disabled={removeMut.isPending}
                onClick={() => removeMut.mutate({ companyId, atelierId: a.id })}
                className="rounded border border-red-500/40 px-2 py-0.5 text-xs text-red-300 hover:border-red-500"
              >
                ✕
              </button>
            </span>
          </div>
          {editId === a.id && (
            <div className="flex flex-wrap gap-1.5 pl-2">
              <input
                value={editRemise}
                onChange={(e) => setEditRemise(e.target.value)}
                placeholder={t("customers.atelierRemisePh")}
                className={`${inp} w-32`}
                data-testid="atelier-remise"
              />
              <input
                value={editAliases}
                onChange={(e) => setEditAliases(e.target.value)}
                placeholder={t("customers.atelierAliasesPh")}
                className={`${inp} flex-1`}
                data-testid="atelier-aliases"
              />
              <button
                type="button"
                disabled={updateMut.isPending || !pctOk(editRemise)}
                onClick={() =>
                  updateMut.mutate(
                    {
                      companyId,
                      atelierId: a.id,
                      retentionPct: editRemise === "" ? null : Number(editRemise),
                      aliases: splitAliases(editAliases),
                    },
                    { onSuccess: () => setEditId(null) },
                  )
                }
                className="rounded-md bg-lime px-2.5 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
                data-testid="atelier-save"
              >
                {t("customers.atelierSaveBtn")}
              </button>
              {updateMut.error && (
                <p className="w-full text-xs text-red-400">{updateMut.error.message}</p>
              )}
            </div>
          )}
        </div>
      ))}
      <div className="flex gap-1.5">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("customers.atelierNamePh")}
          className="min-w-0 flex-1 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm outline-none focus:border-lime"
        />
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder={t("customers.atelierCityPh")}
          className="w-32 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm outline-none focus:border-lime"
        />
        <input
          value={remise}
          onChange={(e) => setRemise(e.target.value)}
          placeholder={t("customers.atelierRemisePh")}
          className="w-28 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm outline-none focus:border-lime"
        />
        <input
          value={aliases}
          onChange={(e) => setAliases(e.target.value)}
          placeholder={t("customers.atelierAliasesPh")}
          className="min-w-0 flex-1 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm outline-none focus:border-lime"
        />
        <button
          type="button"
          disabled={!name.trim() || addMut.isPending || !pctOk(remise)}
          onClick={() => {
            addMut.mutate({
              companyId,
              customerId,
              name: name.trim(),
              city: city.trim() || undefined,
              retentionPct: remise === "" ? null : Number(remise),
              aliases: splitAliases(aliases),
            });
            setName("");
            setCity("");
            setRemise("");
            setAliases("");
          }}
          className="shrink-0 rounded-md border border-lime/50 px-2.5 py-1.5 text-xs font-semibold text-lime hover:bg-lime/10 disabled:opacity-40"
        >
          + {t("customers.atelierAddBtn")}
        </button>
      </div>
    </div>
  );
}
